import type { UseQueryResult } from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "./use-query-client";
import {
  fetchChatbotDefaultQueries,
  fetchComponentDetailsFromServer,
  sendMessageToChatbot,
} from "@/services/chatbot-service";
import type {
  ChatbotDefaultQueriesPayload,
  ChatbotDefaultQuestions,
  ChatbotMemberSpecificPayloadType,
  ChatbotMessageType,
  ChatbotResponse,
  ChatbotSendMessageArgs,
} from "@/types";
import {
  getCurrentTimestamp,
  getMatchingComponentKey,
  getUniqueId,
  hasAtLeastOneComponentAsKey,
  isValidBenefitDocument,
  isValidMemberMedication,
  isValidMemberScope,
  isValidProviderDetails,
} from "@/lib/chatbot-utils";
import {
  CHATBOT_COMPONENT_NAME,
  CHATBOT_DEFAULT_REPLY,
  QueryKeys,
} from "@/constants";
import useUserInfo from "./use-user-info";

const DEFAULT_QUERIES = [
  {
    data: [
      "What is the overall deductible?",
      "What is the out-of-pocket limit for this plan?",
    ],
    header: "Basic coverage and limits",
    id: 0,
  },
  {
    data: [
      "How much is Major Diagnostic and Imaging?",
      "How much are X-Ray and Diagnostic?",
    ],
    header: "Lab/Radiology",
    id: 1,
  },
  {
    data: [
      "How much are Primary Care Office Visits?",
      "How much are Preventive Care Services?",
      "How much are urgent care services?",
    ],
    header: "Medical Services",
    id: 2,
  },
];

const useChatbotDefaultQuestionsQuery = (): UseQueryResult<
  ChatbotDefaultQuestions,
  Error
> => {
  const userInfo = useUserInfo();
  const chatbotDefaultQueriesPayload: ChatbotDefaultQueriesPayload = {
    loginGroupId: userInfo.loginGroupId,
    divisionId: userInfo.divisionId,
    groupId: userInfo.groupId,
    memberId: userInfo.memberId,
    planId: userInfo.planId,
    token: userInfo.accessToken,
  };
  return useQuery({
    queryKey: [QueryKeys.CHATBOT_DEFAULT_QUERIES],
    queryFn: async () => {
      const response = await fetchChatbotDefaultQueries(
        chatbotDefaultQueriesPayload,
      );
      return response.data;
    },
    staleTime: Infinity,
    enabled: Boolean(chatbotDefaultQueriesPayload?.token),
  });
};

const useChatbotQuery = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: ChatbotMessageType) => void;
  onError: (error: Error) => void;
}) => {
  return useMutation<ChatbotResponse, Error, ChatbotSendMessageArgs>({
    mutationFn: (question) => sendMessageToChatbot(question),
    onSuccess: (response) => {
      const defaultQueries = queryClient.getQueryData<ChatbotDefaultQuestions>([
        QueryKeys.CHATBOT_DEFAULT_QUERIES,
      ]);
      let botReply: ChatbotMessageType;
      const sharedBotReply = {
        id: getUniqueId(),
        isUser: false,
        containsComponentToRender: false,
        timestamp: getCurrentTimestamp(new Date()),
      };
      if (Object.keys(response.data.response).length > 0) {
        if (response.data.follow_up_questions.length > 0) {
          // set follow up questions in related questions
          botReply = {
            ...sharedBotReply,
            textMessage: response.data.response.answer,
            relatedQuestions: response.data.follow_up_questions,
          };
        } else {
          // set default questions in related questions
          botReply = {
            ...sharedBotReply,
            textMessage: response.data.response.answer,
            relatedQuestions:
              response?.data.response.category === "chitchat"
                ? defaultQueries
                  ? defaultQueries
                  : DEFAULT_QUERIES
                : [],
          };
        }
      } else {
        // actual response from bot not available
        if (response.data.recommended_matches?.length > 0) {
          botReply = {
            ...sharedBotReply,
            textMessage: CHATBOT_DEFAULT_REPLY,
            containsComponentToRender: true,
            component: {
              componentName: CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS,
            },
            relatedQuestions: response.data.recommended_matches,
          };
        } else {
          botReply = {
            ...sharedBotReply,
            textMessage: CHATBOT_DEFAULT_REPLY,
            containsComponentToRender: true,
            component: {
              componentName: CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS,
            },
            relatedQuestions: defaultQueries ? defaultQueries : DEFAULT_QUERIES,
          };
        }
      }
      onSuccess(botReply);
    },
    onError: (error) => {
      onError(error);
    },
  });
};

const useMemberSpecificQuery = ({
  onSuccess,
  onError,
}: {
  onSuccess: (response: ChatbotMessageType) => void;
  onError: (error: Error) => void;
}) => {
  /**
   * response type set to 'any' since member specific endpoint has no structured response format even for successful response
   * this requires proper implementation of response from API for member specific endpoint to set defined or union type
   **/
  return useMutation<unknown, Error, ChatbotMemberSpecificPayloadType>({
    mutationFn: (memberSpecificPayload) =>
      fetchComponentDetailsFromServer(memberSpecificPayload),
    onSuccess: (response, payload) => {
      let memberSpecificReply: ChatbotMessageType;
      const sharedMemberSpecificReply = {
        id: getUniqueId(),
        isUser: false,
        timestamp: getCurrentTimestamp(new Date()),
      };
      const defaultReply: ChatbotMessageType = {
        ...sharedMemberSpecificReply,
        textMessage: CHATBOT_DEFAULT_REPLY,
        containsComponentToRender: true,
        component: {
          componentName: CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS,
        },
      };
      if (
        response.summary.status_code === "REQ-200" &&
        response.summary.status_message === "success"
      ) {
        if (hasAtLeastOneComponentAsKey(response.data)) {
          const componentName = getMatchingComponentKey(response.data);
          switch (componentName) {
            case CHATBOT_COMPONENT_NAME.PLAN_SERVICE_DETAILS: {
              const planServiceDetailData = response.data;
              const hasInNetwork = Object.prototype.hasOwnProperty.call(
                planServiceDetailData[
                  CHATBOT_COMPONENT_NAME.PLAN_SERVICE_DETAILS
                ],
                "in_network_pay",
              );
              const hasOutNetwork = Object.prototype.hasOwnProperty.call(
                planServiceDetailData[
                  CHATBOT_COMPONENT_NAME.PLAN_SERVICE_DETAILS
                ],
                "out_network_pay",
              );
              const hasWidget =
                Object.prototype.hasOwnProperty.call(
                  planServiceDetailData,
                  "widget",
                ) &&
                Array.isArray(planServiceDetailData.widget) &&
                planServiceDetailData.widget.length > 0;
              const hasMoreThanSingleKey =
                Object.keys(
                  planServiceDetailData[
                    CHATBOT_COMPONENT_NAME.PLAN_SERVICE_DETAILS
                  ],
                ).length > 1;
              const textMessage = hasInNetwork
                ? planServiceDetailData[
                    CHATBOT_COMPONENT_NAME.PLAN_SERVICE_DETAILS
                  ]["in_network_pay"]
                : planServiceDetailData[
                    CHATBOT_COMPONENT_NAME.PLAN_SERVICE_DETAILS
                  ]["out_network_pay"];
              if (
                (hasMoreThanSingleKey && (hasInNetwork || hasOutNetwork)) ||
                hasWidget
              ) {
                memberSpecificReply = {
                  ...sharedMemberSpecificReply,
                  textMessage,
                  ...(hasWidget && {
                    component: {
                      widget: planServiceDetailData.widget,
                      componentName:
                        CHATBOT_COMPONENT_NAME.PLAN_SERVICE_DETAILS,
                    },
                    containsComponentToRender: true,
                  }),
                  relatedQuestions:
                    payload?.relatedQuestions!.length > 0
                      ? payload?.relatedQuestions
                      : [],
                };
                onSuccess(memberSpecificReply);
              } else {
                onSuccess(defaultReply);
              }
              break;
            }
            case CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS: {
              const providerDetailsData = response.data;
              if (isValidProviderDetails(providerDetailsData)) {
                memberSpecificReply = {
                  ...sharedMemberSpecificReply,
                  textMessage: `Your Provider is ${providerDetailsData[CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS].service_provider}`,
                  component: {
                    widget: providerDetailsData.widget,
                    componentName: CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS,
                    componentData:
                      providerDetailsData[
                        CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS
                      ],
                  },
                  containsComponentToRender: true,
                  relatedQuestions:
                    payload?.relatedQuestions!.length > 0
                      ? payload?.relatedQuestions
                      : [],
                };
                onSuccess(memberSpecificReply);
              } else {
                onSuccess(defaultReply);
              }
              break;
            }
            case CHATBOT_COMPONENT_NAME.BENEFIT_DOCUMENT: {
              const benefitDocumentData = response.data;
              if (isValidBenefitDocument(benefitDocumentData)) {
                memberSpecificReply = {
                  ...sharedMemberSpecificReply,
                  textMessage: "",
                  component: {
                    widget: benefitDocumentData?.widget,
                    componentName: CHATBOT_COMPONENT_NAME.BENEFIT_DOCUMENT,
                    componentData:
                      benefitDocumentData[
                        CHATBOT_COMPONENT_NAME.BENEFIT_DOCUMENT
                      ],
                  },
                  containsComponentToRender: true,
                  relatedQuestions:
                    payload?.relatedQuestions!.length > 0
                      ? payload?.relatedQuestions
                      : [],
                };
                onSuccess(memberSpecificReply);
              } else {
                onSuccess(defaultReply);
              }
              break;
            }
            case CHATBOT_COMPONENT_NAME.MEMBER_SCOPE: {
              const memberScopeData = response.data;
              if (isValidMemberScope(memberScopeData)) {
                memberSpecificReply = {
                  ...sharedMemberSpecificReply,
                  textMessage:
                    memberScopeData[CHATBOT_COMPONENT_NAME.MEMBER_SCOPE]
                      ?.content,
                  containsComponentToRender: false,
                  relatedQuestions:
                    payload?.relatedQuestions!.length > 0
                      ? payload?.relatedQuestions
                      : [],
                };
                onSuccess(memberSpecificReply);
              }
              break;
            }
            case CHATBOT_COMPONENT_NAME.MEMBER_MEDICATION: {
              const memberMedicationData = response.data;
              if (isValidMemberMedication(memberMedicationData)) {
                memberSpecificReply = {
                  ...sharedMemberSpecificReply,
                  textMessage: memberMedicationData.content,
                  component: {
                    widget: memberMedicationData?.widget,
                    componentName: CHATBOT_COMPONENT_NAME.MEMBER_MEDICATION,
                    componentData:
                      memberMedicationData[
                        CHATBOT_COMPONENT_NAME.MEMBER_MEDICATION
                      ],
                  },
                  containsComponentToRender: true,
                  relatedQuestions:
                    payload?.relatedQuestions!.length > 0
                      ? payload?.relatedQuestions
                      : [],
                };
                onSuccess(memberSpecificReply);
              } else {
                onSuccess(defaultReply);
              }
              break;
            }
          }
        } else {
          const isWidgetAvailable =
            Object.prototype.hasOwnProperty.call(response?.data, "widget") &&
            Array.isArray(response?.data?.widget) &&
            response?.data?.widget.length > 0;
          if (isWidgetAvailable) {
            memberSpecificReply = {
              ...sharedMemberSpecificReply,
              textMessage: "",
              containsComponentToRender: true,
              component: {
                widget: response?.data?.widget,
              },
            };
            onSuccess(memberSpecificReply);
          } else {
            onSuccess(defaultReply);
          }
        }
      }
    },
    onError: (error) => {
      onError(error);
    },
  });
};

export {
  useChatbotDefaultQuestionsQuery,
  useChatbotQuery,
  useMemberSpecificQuery,
};
